import React, { useState } from "react";
import moment from "moment";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ChevronLeft, ChevronRight, Copy, Check, TrendingUp, TrendingDown } from "lucide-react";

// Shared building blocks for the Customers and Service Providers pages.

export const FONT_HEADING = { fontFamily: "'NeueMontreal', sans-serif" };
export const RED = "#EA352B";
export const PAGE_SIZES = [10, 20, 50];

export const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  const m = moment.utc(value);
  if (!m.isValid()) return "-";
  return m.format(withTime ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY");
};

export const fromNow = (value) => {
  const m = moment.utc(value);
  return m.isValid() ? m.fromNow() : "";
};

export const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

export const handleAuthError = (error) => {
  const status = error?.response?.status;
  const msg = error?.response?.data?.msg;
  if (status === 401 || msg === "Invalid Token") {
    localStorage.clear();
    window.location.href = "/admin-login";
    return true;
  }
  return false;
};

// { thisMonth, monthChange } — signups this calendar month and % vs last month
export const joinedThisMonth = (list) => {
  const startOfMonth = moment().startOf("month");
  const startOfLastMonth = moment().subtract(1, "month").startOf("month");
  const thisMonth = list.filter((c) => moment.utc(c.createdAt).isSameOrAfter(startOfMonth)).length;
  const lastMonth = list.filter((c) => {
    const d = moment.utc(c.createdAt);
    return d.isSameOrAfter(startOfLastMonth) && d.isBefore(startOfMonth);
  }).length;
  return { thisMonth, monthChange: lastMonth ? Math.round(((thisMonth - lastMonth) / lastMonth) * 100) : null };
};

export const monthlySignups = (list, months = 6) =>
  Array.from({ length: months }, (_, i) => moment().subtract(months - 1 - i, "months").startOf("month")).map((m) => ({
    name: m.format("MMM"),
    signups: list.filter((c) => moment.utc(c.createdAt).isSame(m, "month")).length,
  }));

export function SignupsChart({ data, loading }) {
  return (
    <div className="h-[140px] -mx-2">
      {loading ? (
        <div className="h-full mx-2 rounded-xl bg-[#F3F3F6] animate-pulse" />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 11 }} />
            <Tooltip
              cursor={{ fill: "#F7F7F9" }}
              content={({ active, payload, label }) =>
                active && payload?.length ? (
                  <div className="rounded-lg bg-[#1B1B22] text-[#fff] px-2.5 py-1.5 text-xs">
                    {label}: <b>{payload[0].value}</b> signups
                  </div>
                ) : null
              }
            />
            <Bar dataKey="signups" radius={[6, 6, 0, 0]} maxBarSize={26}>
              {data.map((d, i) => (
                <Cell key={d.name} fill={i === data.length - 1 ? RED : "#F6C9C5"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function Kpi({ Icon, label, value, sub, change, loading }) {
  return (
    <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-5 flex items-center gap-4" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
      <span className="w-12 h-12 rounded-2xl bg-[#FDECEA] flex items-center justify-center shrink-0">
        <Icon size={20} className="text-[#EA352B]" />
      </span>
      <div className="min-w-0">
        <p className="m-0 text-xs font-medium text-[#6B7280]">{label}</p>
        {loading ? (
          <div className="h-7 w-16 mt-1 rounded bg-[#F3F3F6] animate-pulse" />
        ) : (
          <p className="m-0 mt-0.5 text-2xl font-semibold text-[#111]">{Number(value).toLocaleString()}</p>
        )}
        {!loading && change != null && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${change >= 0 ? "text-[#16A34A]" : "text-[#EA352B]"}`}>
            {change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {change >= 0 ? "+" : ""}{change}% vs last month
          </span>
        )}
        {!loading && sub && <p className="m-0 text-[11px] text-[#9CA3AF]">{sub}</p>}
      </div>
    </div>
  );
}

export function SideCard({ title, Icon, children }) {
  return (
    <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-5 min-w-0" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-lg bg-[#FDECEA] flex items-center justify-center"><Icon size={15} className="text-[#EA352B]" /></span>
        <h3 className="m-0 text-base font-medium text-[#111]" style={FONT_HEADING}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

export function MiniStat({ color, label, value }) {
  return (
    <div className="rounded-xl bg-[#F7F7F9] px-3 py-2">
      <span className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} /> {label}
      </span>
      <span className="block text-base font-semibold text-[#111]">{value.toLocaleString()}</span>
    </div>
  );
}

export function Field({ Icon, label, value, copy, mono }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div className="flex items-center gap-3 rounded-xl bg-[#F7F7F9] px-3 py-2.5 min-w-0">
      <Icon size={15} className="text-[#EA352B] shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="m-0 text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</p>
        <p className={`m-0 text-[13px] truncate ${value ? "text-[#111] font-medium" : "text-[#C0C4CC]"} ${mono ? "font-mono text-[12px]" : ""}`} title={value || undefined}>
          {value || "Not provided"}
        </p>
      </div>
      {copy && value && (
        <button type="button" onClick={doCopy} aria-label={`Copy ${label}`} className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[#9CA3AF] hover:bg-[#fff] hover:text-[#EA352B] transition">
          {copied ? <Check size={14} className="text-[#16A34A]" /> : <Copy size={14} />}
        </button>
      )}
    </div>
  );
}

export function Pagination({ page, total, onChange }) {
  // 1 … 4 5 6 … 12
  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || Math.abs(i - page) <= 1) pages.push(i);
    else if (pages[pages.length - 1] !== "…") pages.push("…");
  }
  return (
    <nav className="flex items-center gap-1" aria-label="Pagination">
      <PageBtn disabled={page === 1} onClick={() => onChange(page - 1)} label="Previous page"><ChevronLeft size={15} /></PageBtn>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`e${i}`} className="w-8 text-center text-xs text-[#9CA3AF]">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-current={p === page ? "page" : undefined}
            className={`w-8 h-8 rounded-lg text-xs font-semibold transition ${p === page ? "bg-[#EA352B] text-[#fff] shadow-sm" : "text-[#6B7280] hover:bg-[#F3F3F6]"}`}
          >
            {p}
          </button>
        )
      )}
      <PageBtn disabled={page === total} onClick={() => onChange(page + 1)} label="Next page"><ChevronRight size={15} /></PageBtn>
    </nav>
  );
}

export function PageBtn({ disabled, onClick, label, children }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className="w-8 h-8 rounded-lg flex items-center justify-center text-[#374151] border border-[#EEEEF1] hover:border-[#EA352B] hover:text-[#EA352B] disabled:opacity-40 disabled:pointer-events-none transition"
    >
      {children}
    </button>
  );
}

export function Td({ children, className = "" }) {
  return <td className={`py-3 px-3 first:pl-0 text-sm text-[#4B5563] border-b border-[#F3F3F6] whitespace-nowrap ${className}`}>{children}</td>;
}

export function EmptyState({ Icon, title, text, action }) {
  return (
    <div className="py-14 text-center">
      <span className="mx-auto w-14 h-14 rounded-2xl bg-[#F3F3F6] flex items-center justify-center">
        <Icon size={22} className="text-[#9CA3AF]" />
      </span>
      <p className="m-0 mt-3 text-sm font-semibold text-[#111]">{title}</p>
      <p className="m-0 mt-1 text-xs text-[#9CA3AF]">{text}</p>
      {action}
    </div>
  );
}

export function RowSkeleton({ widths = [20, 22, 24, 16, 4] }) {
  return (
    <tr>
      <td className="py-3 pr-3 border-b border-[#F3F3F6]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F3F3F6] animate-pulse" />
          <div>
            <div className="h-3 w-28 bg-[#F3F3F6] rounded animate-pulse mb-2" />
            <div className="h-2 w-36 bg-[#F3F3F6] rounded animate-pulse" />
          </div>
        </div>
      </td>
      {widths.map((w, i) => (
        <td key={i} className="py-3 px-3 border-b border-[#F3F3F6]">
          <div className="h-3 bg-[#F3F3F6] rounded animate-pulse" style={{ width: `${w * 4}px` }} />
        </td>
      ))}
    </tr>
  );
}

export function CardSkeleton({ compact }) {
  return (
    <div className={`flex items-center gap-3 ${compact ? "p-2.5" : "rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] p-4"}`}>
      <div className="w-10 h-10 rounded-xl bg-[#F3F3F6] animate-pulse shrink-0" />
      <div className="flex-1">
        <div className="h-3 w-24 bg-[#F3F3F6] rounded animate-pulse mb-2" />
        <div className="h-2 w-16 bg-[#F3F3F6] rounded animate-pulse" />
      </div>
    </div>
  );
}
